import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'focuspips-theme';
const getThemeKey = (userId?: string) => userId ? `focuspips-theme_${userId}` : THEME_STORAGE_KEY;

const isValidTheme = (value: string | null): value is Theme => value === 'dark' || value === 'light';

const getStoredTheme = (userId?: string): Theme | null => {
  const userTheme = userId ? localStorage.getItem(getThemeKey(userId)) : null;
  if (isValidTheme(userTheme)) {
    return userTheme;
  }

  const sharedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  if (isValidTheme(sharedTheme)) {
    return sharedTheme;
  }

  return null;
};

const persistThemeLocally = (newTheme: Theme, userId?: string) => {
  localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  if (userId) {
    localStorage.setItem(getThemeKey(userId), newTheme);
  }
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => {
    const savedTheme = getStoredTheme();
    if (savedTheme) {
      return savedTheme;
    }
    
    // Si pas de thème sauvegardé, utiliser la préférence système
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'dark'; // Thème par défaut
  });

  // Fonction pour récupérer les préférences depuis Firebase
  const loadThemeFromFirebase = async () => {
    if (!user?.uid) return;
    
    try {
      const userPrefsRef = doc(db, 'userPreferences', user.uid);
      const userPrefsSnap = await getDoc(userPrefsRef);
      
      if (userPrefsSnap.exists()) {
        const data = userPrefsSnap.data();
        if (data.theme && (data.theme === 'dark' || data.theme === 'light')) {
          const firebaseTheme = data.theme as Theme;

          const localTheme = getStoredTheme(user.uid);
          if (localTheme !== firebaseTheme) {
            setThemeState(firebaseTheme);
            persistThemeLocally(firebaseTheme, user.uid);
            applyTheme(firebaseTheme);
          }
        }
      }
    } catch (error) {
      // console.error supprimé
    }
  };

  // Fonction pour sauvegarder le thème dans Firebase
  const saveThemeToFirebase = async (newTheme: Theme) => {
    if (!user?.uid) return;
    
    try {
      const userPrefsRef = doc(db, 'userPreferences', user.uid);
      
      // Vérifier si le document existe
      const userPrefsSnap = await getDoc(userPrefsRef);
      
      if (userPrefsSnap.exists()) {
        // Mettre à jour le document existant
        await updateDoc(userPrefsRef, {
          theme: newTheme
        });
      } else {
        // Créer un nouveau document avec le thème
        await setDoc(userPrefsRef, {
          theme: newTheme
        });
      }
    } catch (error) {
      // console.error supprimé
    }
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    persistThemeLocally(newTheme, user?.uid);
    applyTheme(newTheme);
    
    // Sauvegarder dans Firebase de manière asynchrone
    saveThemeToFirebase(newTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };

  // Source de vérité du thème : on synchronise <html> ET <body>. Les landing pages
  // posent `dark` sur <body> en se démontant ; si on ne nettoyait que <html>,
  // `.dark …` (Tailwind darkMode class, .dark .settings-panel…) continuerait de
  // matcher via <body> et une partie de l'UI resterait sombre jusqu'au refresh.
  const applyTheme = (newTheme: Theme) => {
    const targets = [document.documentElement, document.body];

    targets.forEach((el) => {
      if (newTheme === 'light') {
        el.classList.remove('dark');
      } else {
        el.classList.add('dark');
      }
      el.style.colorScheme = newTheme;
    });
  };

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Charger le thème depuis Firebase quand l'utilisateur se connecte
  useEffect(() => {
    if (user?.uid) {
      const localTheme = getStoredTheme(user.uid);
      if (localTheme) {
        setThemeState(localTheme);
        persistThemeLocally(localTheme, user.uid);
        applyTheme(localTheme);
        saveThemeToFirebase(localTheme);
      } else {
        loadThemeFromFirebase();
      }
    }
  }, [user?.uid]);

  // Écouter les changements de préférence système
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Ne changer que si aucun thème n'est explicitement défini
      const savedTheme = getStoredTheme(user?.uid);
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const value = {
    theme,
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
} 
